import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import back_buttonImg from "../../assets/images/back_button.png";
import green_logoImg from "../../assets/images/green_logo.png";
import { gameFileUrl } from "../../services/offlineGames/gameFiles";
import { getOfflineGame } from "../../services/offlineGames/registry";
import { isControlledByOfflineWorker } from "../../services/offlineGames/serviceWorker";
import useOnlineStatus from "../../services/offlineGames/useOnlineStatus";

const RELOAD_FLAG = "akedo_offline_play_reloaded";

// The page has to be served through the offline worker to reach the downloaded files.
// Right after the worker installs it takes over by itself; a page that was loaded
// around it (e.g. a hard refresh) only gets it on the next load, so reload once.
const useOfflineWorkerControl = () => {
	const [controlled, setControlled] = useState(isControlledByOfflineWorker);
	useEffect(() => {
		if (controlled) {
			sessionStorage.removeItem(RELOAD_FLAG);
			return undefined;
		}
		if (!navigator.serviceWorker) return undefined;
		const update = () => setControlled(isControlledByOfflineWorker());
		navigator.serviceWorker.addEventListener("controllerchange", update);
		navigator.serviceWorker.getRegistration().then((registration) => {
			if (registration?.active && !isControlledByOfflineWorker() && !sessionStorage.getItem(RELOAD_FLAG)) {
				sessionStorage.setItem(RELOAD_FLAG, "1");
				window.location.reload();
			}
		});
		return () => navigator.serviceWorker.removeEventListener("controllerchange", update);
	}, [controlled]);
	return controlled;
};

const PLAYED_GAMES = "/settings?tab=played";

const Message = ({ children, onBack }) => (
	<div className="min-h-screen flex flex-col items-center justify-center gap-4 text-center px-6">
		<p className="text-white">{children}</p>
		<button className="text-[#00e6F4] uppercase" onClick={onBack}>
			Back to played games
		</button>
	</div>
);

// Plays a downloaded game from the offline cache (served by public/sw.js).
const OfflinePlay = () => {
	const { appId } = useParams();
	const navigate = useNavigate();
	const game = getOfflineGame(appId);
	const controlled = useOfflineWorkerControl();
	const isOnline = useOnlineStatus();
	// Back to where the game was opened from (a game card, Settings), or Played Games.
	const backToList = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(PLAYED_GAMES));

	if (!game) return <Message onBack={backToList}>This game isn't downloaded on this device.</Message>;
	if (!controlled) {
		// Online, the worker is still starting up. Offline, it's usually a hard reload (Cmd+Shift+R), which skips it.
		return isOnline ? (
			<Message onBack={backToList}>Getting the game ready…</Message>
		) : (
			<Message onBack={() => window.location.assign(PLAYED_GAMES)}>
				Couldn't open the downloaded game. Go back and try again (use a normal refresh, not a hard refresh).
			</Message>
		);
	}

	const token = localStorage.getItem("token") || "";
	const src = `${gameFileUrl(game.id, "index.html")}?token=${encodeURIComponent(token)}`;

	return (
		<div style={{ width: "100%", height: "100dvh", position: "relative", overflow: "hidden" }}>
			<iframe src={src} title={game.name} style={{ border: "none", width: "100%", height: "100%" }} allow="autoplay; fullscreen" />
			{/* Same back tab as the regular game page (Play.jsx). */}
			<button
				className="fixed z-[999] left-0 top-[20px] flex items-center gap-[4px]"
				onClick={backToList}
				aria-label="Back"
				style={{
					padding: "15px 10px",
					borderRadius: "0 7px 7px 0",
					border: "2px solid rgba(255, 255, 255, 0.75)",
					marginLeft: "-2px",
					backgroundColor: "black",
					opacity: 0.7,
				}}
			>
				<img src={back_buttonImg} alt="" style={{ height: "20px" }} />
				<img src={green_logoImg} alt="" style={{ height: "20px" }} />
			</button>
		</div>
	);
};

export default OfflinePlay;
